import type React from 'react';
import { CalendarX2, ChevronRight, Copy } from 'lucide-react';
import { Link } from 'react-router';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import type { CampaignDto } from '@/generated/korza-api-client/types.gen';
import { useTenant } from '@/hooks/use-tenant';
import { useI18nContext } from '@/i18n/i18n-react';
import { canCancelCampaign } from '@/lib/campaign-list';
import { cn } from '@/lib/utils';

const rowClassName =
  'flex min-h-touch w-full items-center gap-3 rounded-lg px-3 text-left text-sm font-medium outline-none transition-colors hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring';

/**
 * Actions of a campaign card on mobile (PUSH-04 AC 2): Duplicar, Cancelar
 * (only while not sent) and Ver detalhes. Picking one closes the sheet.
 */
export function CampaignActionsSheet({
  campaign,
  open,
  onOpenChange,
  onDuplicate,
  onCancel,
  returnFocusRef,
}: {
  campaign: CampaignDto | null;
  open: boolean;
  onOpenChange(open: boolean): void;
  onDuplicate(campaign: CampaignDto): void;
  onCancel(campaign: CampaignDto): void;
  returnFocusRef?: React.RefObject<HTMLElement | null>;
}) {
  const { LL } = useI18nContext();
  const tenant = useTenant();

  const pick = (action: (campaign: CampaignDto) => void) => () => {
    if (!campaign) return;
    onOpenChange(false);
    action(campaign);
  };

  return (
    <Drawer open={open && campaign !== null} onOpenChange={onOpenChange}>
      <DrawerContent returnFocusRef={returnFocusRef}>
        <DrawerHeader className="pb-2 text-left">
          <DrawerTitle className="truncate">{campaign?.name}</DrawerTitle>
          <DrawerDescription className="sr-only">
            {LL.campaigns.actionsSheet.description()}
          </DrawerDescription>
        </DrawerHeader>
        {campaign && (
          <div className="flex flex-col gap-1 px-2 pb-4">
            <button
              type="button"
              className={rowClassName}
              onClick={pick(onDuplicate)}
            >
              <Copy className="size-4" aria-hidden />
              {LL.campaigns.actionsSheet.duplicate()}
            </button>
            {canCancelCampaign(campaign) && (
              <button
                type="button"
                className={cn(rowClassName, 'text-destructive')}
                onClick={pick(onCancel)}
              >
                <CalendarX2 className="size-4" aria-hidden />
                {LL.campaigns.actionsSheet.cancel()}
              </button>
            )}
            <Link
              to={`/${tenant.slug}/campaigns/${campaign.id}`}
              className={rowClassName}
              onClick={() => onOpenChange(false)}
            >
              <ChevronRight className="size-4" aria-hidden />
              {LL.campaigns.actionsSheet.viewDetails()}
            </Link>
          </div>
        )}
      </DrawerContent>
    </Drawer>
  );
}
